import { useNavigate } from 'react-router-dom';
import { useDiscovery } from '../context/DiscoveryContext';
import { getPublicGeniesForSubcategory } from '@/data/discovery/genie-catalog';
import { getBrandForPersona } from '@/data/genies/persona-brands';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PhoneMockup } from './PhoneMockup';
import { trackEvent } from '@/lib/ga4';

export const GenieRecommendationsStep = () => {
  const navigate = useNavigate();
  const { state } = useDiscovery();

  if (!state.industry || !state.subcategory) {
    return null;
  }

  const { subcategory } = state;
  const hasSubcategoryIcon = !!subcategory.iconPath;
  const SubcategoryIcon = subcategory.icon;

  const genies = getPublicGeniesForSubcategory(subcategory.id);
  const displayGenies = genies.slice(0, 5);

  const openDemo = (genieId: string) => {
    trackEvent('discover_demo_opened', {
      industry: state.industry?.id,
      subcategory: state.subcategory?.id,
      persona_id: genieId,
    });
    navigate(`/discover/demo/${genieId}`);
  };

  const getLogoPath = (personaId: string): string | null => {
    const brand = getBrandForPersona(personaId);
    if (!brand) return null;
    const safeName = brand.brandName.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    return `/brand-logos/${safeName}-stacked.png`;
  };

  return (
    <div className="text-center">
      {/* Header */}
      <div className="mb-4 sm:mb-8">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500/30 to-blue-500/30 backdrop-blur-sm rounded-full mb-4">
          {hasSubcategoryIcon ? (
            <img src={subcategory.iconPath} alt={subcategory.name} className="w-5 h-5 object-contain" />
          ) : (
            <SubcategoryIcon className="w-5 h-5 text-white" />
          )}
          <span className="text-sm font-medium text-white">{subcategory.name}</span>
        </div>

        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">
          Your {subcategory.name} Genies
        </h1>
        <p className="text-base text-white/70 max-w-lg mx-auto">
          Tap any genie to try a live voice demo, or create your own in one click
        </p>
      </div>

      {/* Phone mockup gallery */}
      <div className="relative mb-4 sm:mb-8">
        <div className="flex sm:flex-wrap sm:justify-center gap-4 sm:gap-5 lg:gap-6 sm:max-w-[1400px] sm:mx-auto overflow-x-auto sm:overflow-x-visible -mx-4 px-4 sm:mx-0 sm:px-0 pb-3 sm:pb-0 snap-x snap-mandatory sm:snap-none scroll-smooth">
          {displayGenies.map((genie) => {
            const brand = getBrandForPersona(genie.id);
            return (
              <div key={genie.id} className="flex flex-col items-center gap-3 flex-shrink-0 sm:flex-shrink snap-center sm:snap-align-none">
                {/* Popular badge */}
                {genie.isFeatured && (
                  <Badge className="bg-gradient-to-r from-purple-500 to-blue-500 text-white text-[10px] px-2.5 py-0.5 border-0">
                    Popular
                  </Badge>
                )}
                {!genie.isFeatured && <div className="h-5" />}

                <PhoneMockup
                  genie={genie}
                  brand={brand}
                  logoPath={getLogoPath(genie.id)}
                  onClick={() => openDemo(genie.id)}
                />

                {/* Name + role */}
                <div className="text-center max-w-[240px]">
                  <h3 className="text-base font-bold text-white leading-tight">{genie.name}</h3>
                  <p className="text-sm text-white/50 mt-0.5 line-clamp-1">{genie.role}</p>
                </div>

                {/* Use This Genie CTA */}
                <Button
                  size="sm"
                  onClick={() => openDemo(genie.id)}
                  className="bg-purple-500/80 hover:bg-purple-500 text-white text-sm font-medium"
                >
                  Use This Genie
                  <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Empty state */}
      {displayGenies.length === 0 && (
        <div className="text-white/50 text-sm py-8">
          No genies available yet for this business type. Check back soon!
        </div>
      )}

      {/* Bottom CTA */}
      <div className="flex items-center justify-center gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/live-agent/business-onboarding')}
          className="border-white/30 text-white hover:bg-white/10 hover:border-white/50 bg-transparent text-sm"
        >
          Talk to our expert Help Genie
        </Button>
      </div>

    </div>
  );
};
